import useSafeState from "../utils/safe-state";
import {Button, Col, Form, Input, Row, Select, Spin, Radio, Space, Tooltip, Divider} from "antd";
import {InfoCircleTwoTone, LeftOutlined} from "@ant-design/icons";
import React, {useEffect} from "react";
import {Trans} from "@lingui/macro";
import {useAxios} from "../utils/hooks";
import {openNotification} from "../utils/openNotification";
import ButtonWithIcon from "../components/buttons/button-with-icon";

interface ItemDataFormProps {
    data?: any;
    onClose?: () => void;
    onBack?: () => void;
}

const ItemDataForm = (props: ItemDataFormProps) => {

    const axiosInstance = useAxios(process.env.REACT_APP_API_BASE_URL + "");
    const config = {headers: {'Content-Type': 'application/json'}};
    const [form] = Form.useForm();
    const [loading, setLoading] = useSafeState(false);
    const [countries, setCountries] = useSafeState<string[]>([]);
    const itemId = props.data && props.data.itemId;

    useEffect(() => {
        if (itemId) {
            fetchData();
        }
        fetchCountries();
    }, [itemId]);

    const fetchCountries = () => {
        if (axiosInstance.current != null) {
            axiosInstance.current
                .get('/resource/countries/get-all')
                .then(response => {
                    setCountries(response.data);
                })
                .catch(function (error: any) {
                    console.log(error);
                });
        }
    };

    const fetchData = () => {
        if (axiosInstance.current != null) {
            setLoading(true);
            axiosInstance.current
                .get(`/resource/item-data/get-data?itemId=${itemId}`)
                .then(response => {
                    const data = response.data;
                    const formattedData = {
                        ...data,
                        checkedSyntheticRubber: data.checkedSyntheticRubber === 'true',
                        checkedLegacyStock: data.checkedLegacyStock === 'true',
                        goodsMixing: data.goodsMixing ? Number(data.goodsMixing) : undefined,
                        rawMaterialOriginIsClear: data.rawMaterialOriginIsClear ? Number(data.rawMaterialOriginIsClear) : undefined,
                        rawMaterialsContainsWoodPulp: data.rawMaterialsContainsWoodPulp ? Number(data.rawMaterialsContainsWoodPulp) : undefined,
                        rawMaterialGoodsMixing: data.rawMaterialGoodsMixing ? Number(data.rawMaterialGoodsMixing) : undefined,
                        rawMaterialsIntermediaries: data.rawMaterialsIntermediaries ? Number(data.rawMaterialsIntermediaries) : undefined,
                        rawMaterialsChainTransparency: data.rawMaterialsChainTransparency ? Number(data.rawMaterialsChainTransparency) : undefined,
                    };
                    form.setFieldsValue(formattedData);
                })
                .catch(function (error: any) {
                    openNotification('error', error);
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    };

    const handleFinish = (values: any) => {
        if (axiosInstance.current != null) {
            setLoading(true);
            
            const payload = {
                ...values,
                checkedSyntheticRubber: String(values.checkedSyntheticRubber),
                checkedLegacyStock: String(values.checkedLegacyStock),
                goodsMixing: values.goodsMixing ? String(values.goodsMixing) : undefined,
                rawMaterialOriginIsClear: values.rawMaterialOriginIsClear ? String(values.rawMaterialOriginIsClear) : undefined,
                rawMaterialsContainsWoodPulp: values.rawMaterialsContainsWoodPulp ? String(values.rawMaterialsContainsWoodPulp) : undefined,
                rawMaterialGoodsMixing: values.rawMaterialGoodsMixing ? String(values.rawMaterialGoodsMixing) : undefined,
                rawMaterialsIntermediaries: values.rawMaterialsIntermediaries ? String(values.rawMaterialsIntermediaries) : undefined,
                rawMaterialsChainTransparency: values.rawMaterialsChainTransparency ? String(values.rawMaterialsChainTransparency) : undefined,
            };

            if (props.data && props.data.itemFormValues) {
                if (itemId) {
                    // Update the item
                    axiosInstance.current
                        .put(
                            ('/resource/items/' + encodeURIComponent(itemId)),
                            props.data.itemFormValues,
                            config,
                        )
                        .then(() => {
                            // Then save the item data
                            if (axiosInstance.current) {
                                return axiosInstance.current.post(
                                    `/resource/item-data/save-data?itemId=${itemId}`,
                                    payload,
                                    config,
                                );
                            }
                        })
                        .then(() => {
                            openNotification('success', 'Successfully saved.');
                            if (props.onClose) {
                                props.onClose();
                            }
                        })
                        .catch(function (error: any) {
                            openNotification('error', error);
                        })
                        .finally(() => {
                            setLoading(false);
                        });
                } else {
                    axiosInstance.current
                        .post(
                            ('/resource/items'),
                            props.data.itemFormValues,
                            config,
                        )
                        .then((response) => {
                            const newId = response.data.id;
                            if (axiosInstance.current) {
                                return axiosInstance.current.post(
                                    `/resource/item-data/save-data?itemId=${newId}`,
                                    payload,
                                    config,
                                );
                            }
                        })
                        .then(() => {
                            openNotification('success', 'Successfully saved.');
                            if (props.onClose) {
                                props.onClose();
                            }
                        })
                        .catch(function (error: any) {
                            openNotification('error', error);
                        })
                        .finally(() => {
                            setLoading(false);
                        });
                }
            } else {

                axiosInstance.current
                    .post(
                        `/resource/item-data/save-data?itemId=${itemId}`,
                        payload,
                        config,
                    )
                    .then(() => {
                        openNotification('success', 'Successfully saved.');
                        if (props.onClose) {
                            props.onClose();
                        }
                    })
                    .catch(function (error: any) {
                        openNotification('error', error);
                    })
                    .finally(() => {
                        setLoading(false);
                    });
            }
        }
    }

    return (
        <Form
            layout='vertical'
            form={form}
            onFinish={handleFinish}
        >
            <Spin spinning={loading}>
                <b><Trans>1. I declare, that the product I am shipping is 100% made of synthetic rubber.</Trans></b>
                <Form.Item
                    name="checkedSyntheticRubber"
                    rules={[{required: true, message: <Trans>Please select an option</Trans>}]}
                >
                    <Radio.Group>
                        <Space direction="horizontal">
                            <Radio value={true}><Trans>Yes</Trans></Radio>
                            <Radio value={false}><Trans>No</Trans></Radio>
                        </Space>
                    </Radio.Group>
                </Form.Item>

                <Divider/>

                <b><Trans>2. I declare that the part I am shipping is from stock prior to 2025.12.30.?</Trans></b>
                <Form.Item
                    name="checkedLegacyStock"
                    rules={[{required: true, message: <Trans>Please select an option</Trans>}]}
                >
                    <Radio.Group>
                        <Space direction="horizontal">
                            <Radio value={true}><Trans>Yes</Trans></Radio>
                            <Radio value={false}><Trans>No</Trans></Radio>
                        </Space>
                    </Radio.Group>
                </Form.Item>

                <Divider/>

                <b>
                    <Trans>3. What is the country of production?</Trans>
                    <Tooltip
                        placement="top"
                        title={
                            <Trans>
                                ‘country of production’ means the country or territory where the relevant commodity
                                or the relevant commodity used in the production of,
                                or contained in, a relevant product was produced;
                            </Trans>
                        }
                    >
                        <InfoCircleTwoTone twoToneColor="#1890ff" style={{marginLeft: 5}}/>
                    </Tooltip>
                </b>
                <Form.Item
                    name="countryOfProduction"
                    rules={[{required: true, message: <Trans>Please select a country</Trans>}]}
                >
                    <Select
                        showSearch
                        allowClear
                        placeholder={<Trans>Select a country</Trans>}
                        optionFilterProp="children"
                    >
                        {countries.map(country => (
                            <Select.Option key={country} value={country}>
                                {country}
                            </Select.Option>
                        ))}
                    </Select>
                </Form.Item>

                <b><Trans>Provide the part of the country (regio, county, district etc.)</Trans></b>
                <Form.Item
                    name="countryOfProductionRegion"
                    rules={[{required: true, message: <Trans>Please enter the region</Trans>}]}
                >
                    <Input placeholder="regio, county, district etc."/>
                </Form.Item>

                <Divider/>

                <b><Trans>4. Possible to mix with goods originated from negligible or high risk country?</Trans></b>
                <Form.Item
                    name="goodsMixing"
                    rules={[{required: true, message: <Trans>Please select an option</Trans>}]}
                >
                    <Radio.Group>
                        <Space direction="horizontal">
                            <Radio value={1}><Trans>Yes</Trans></Radio>
                            <Radio value={2}><Trans>No</Trans></Radio>
                        </Space>
                    </Radio.Group>
                </Form.Item>

                <Divider/>

                <b><Trans>5. Origin of the raw material is clear?</Trans></b>
                <Form.Item
                    name="rawMaterialOriginIsClear"
                    rules={[{required: true, message: <Trans>Please select an option</Trans>}]}
                >
                    <Radio.Group>
                        <Space direction="horizontal">
                            <Radio value={1}><Trans>Yes</Trans></Radio>
                            <Radio value={2}><Trans>No</Trans></Radio>
                        </Space>
                    </Radio.Group>
                </Form.Item>

                <Divider/>

                <b><Trans>6. Does your product contains wood pulp?</Trans></b>
                <Form.Item
                    name="rawMaterialsContainsWoodPulp"
                    rules={[{required: true, message: <Trans>Please select an option</Trans>}]}
                >
                    <Radio.Group>
                        <Space direction="horizontal">
                            <Radio value={1}><Trans>Yes</Trans></Radio>
                            <Radio value={2}><Trans>No</Trans></Radio>
                        </Space>
                    </Radio.Group>
                </Form.Item>

                <Divider/>

                <b><Trans>7. Is there any risk that different commodities and products (on raw material level) are /
                    could be mixed within the supply chain?</Trans></b>
                <Form.Item
                    name="rawMaterialGoodsMixing"
                    rules={[{required: true, message: <Trans>Please select an option</Trans>}]}
                >
                    <Radio.Group>
                        <Space direction="horizontal">
                            <Radio value={1}><Trans>Yes</Trans></Radio>
                            <Radio value={2}><Trans>No</Trans></Radio>
                        </Space>
                    </Radio.Group>
                </Form.Item>

                <Divider/>

                <b><Trans>8. How many intermediaries are there between the farmland and you?</Trans></b>
                <Form.Item
                    name="rawMaterialsIntermediaries"
                    rules={[{required: true, message: <Trans>Please select an option</Trans>}]}
                >
                    <Radio.Group>
                        <Space direction="horizontal">
                            <Radio value={1}><Trans>1</Trans></Radio>
                            <Radio value={2}><Trans>2</Trans></Radio>
                            <Radio value={3}><Trans>Multiple</Trans></Radio>
                        </Space>
                    </Radio.Group>
                </Form.Item>

                <Divider/>

                <b>
                    <Trans>9. Is the chain transparent and documented or is transparency compromised?</Trans>
                    <Tooltip
                        placement="top"
                        title={
                            <>
                                <div>
                                    <Trans>
                                        Document Type Relevance Bill of materials (BoM) Shows all input components and
                                        their
                                        origin
                                        Chain-of-custody documentation Helps track material through intermediaries
                                        Certificate of origin Indicates country and possibly region of production
                                        Geolocation map + coordinates Required for raw commodity under EUDR
                                        Transport / shipping docs Prove continuity and timing between origin and
                                        processing
                                        Customs or import/export declarations May confirm declared commodity pathways.
                                    </Trans>
                                </div>
                            </>
                        }
                    >
                        <InfoCircleTwoTone twoToneColor="#1890ff" style={{marginLeft: 5}}/>
                    </Tooltip>
                </b>
                <Form.Item
                    name="rawMaterialsChainTransparency"
                    rules={[{required: true, message: <Trans>Please select an option</Trans>}]}
                >
                    <Radio.Group>
                        <Space direction="horizontal">
                            <Radio value={1}><Trans>Transparent and documented</Trans></Radio>
                            <Radio value={2}><Trans>Compromised</Trans></Radio>
                        </Space>
                    </Radio.Group>
                </Form.Item>

            </Spin>

            <div
                style={{
                    position: "fixed",
                    bottom: 0,
                    right: 0,
                    width: 720,
                    background: "#fff",
                    borderTop: "1px solid #e8e8e8",
                    padding: "10px 20px",
                    zIndex: 100,
                    textAlign: "right"
                }}
            >
                {props.onBack && (
                    <Button
                        htmlType="button"
                        style={{marginRight: 8}}
                        onClick={props.onBack}>
                <ButtonWithIcon
                    icon={<LeftOutlined/>}
                    text={<Trans>Back</Trans>}
                />
                </Button>

                )}
                <Button
                    loading={loading}
                    htmlType="submit"
                    type="primary"
                >
                    <Trans>Save</Trans>
                </Button>
            </div>
        </Form>
    );

}
export default ItemDataForm;
